import { useMemo } from 'react';
import { useQuery } from '@supabase-cache-helpers/postgrest-swr';

import { supabase } from 'src/auth/context/supabase/lib';

// ----------------------------------------------------------------------
// need to add co2eq
const defaultColumns =
  'id,created_at,name,is_current, entity_id, workstation, co2eq_per_employee, workstation_percent, number_of_employees, number_of_employees_equipped, co2eq';

export function useGetAllInventoriesFromEntities(entity_id) {
  const { data, isLoading, error, isValidating, mutate } = useQuery(
    supabase
      .from('inventories')
      .select(defaultColumns)
      .eq('entity_id', entity_id)
      .eq('is_deleted', false)
  );

  const memoizedValue = useMemo(
    () => ({
      inventories: data || [],
      inventoriesLoading: isLoading,
      inventoriesError: error,
      inventoriesValidating: isValidating,
      mutateInventories: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useGetByInventoryId(inventories_id, columns = defaultColumns) {
  const { data, isLoading, error, isValidating } = useQuery(
    !inventories_id ? null : supabase.from('inventories').select(columns).eq('id', inventories_id)
  );

  const memoizedValue = useMemo(
    () => ({
      inventories: data || [],
      inventoriesLoading: isLoading,
      inventoriesError: error,
      inventoriesValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useGetCurrentInventoryFromEntity(entity_id) {
  const { data, isLoading, error, isValidating } = useQuery(
    supabase
      .from('inventories')
      .select(defaultColumns)
      .eq('entity_id', entity_id)
      .eq('is_current', true)
  );

  const memoizedValue = useMemo(
    () => ({
      inventories: data || [],
      inventoriesLoading: isLoading,
      inventoriesError: error,
      inventoriesValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function getCurrentInventoryFromEntity(entity_id) {
  const response = await supabase
    .from('inventories')
    .select(defaultColumns)
    .eq('entity_id', entity_id)
    .eq('is_current', true);
  return response.data;
}

// ----------------------------------------------------------------------

export function useGetInventoryById(inventory_id) {
  const { data, isLoading, error, isValidating } = useQuery(
    supabase.from('inventories').select(defaultColumns).eq('id', inventory_id)
  );

  const memoizedValue = useMemo(
    () => ({
      inventories: data || [],
      inventoriesLoading: isLoading,
      inventoriesError: error,
      inventoriesValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function GetInventoryById(inventory_id) {
  const response = await supabase.from('inventories').select(defaultColumns).eq('id', inventory_id);
  return response.data;
}

// ----------------------------------------------------------------------

const entityColumns =
  'id,created_at,name,type,co2eq,workstation,workstation_percent,co2eq_per_employee,number_of_employees,number_of_employees_equipped,logo,parent,is_parent';

// ----------------------------------------------------------------------

export function useGetAllCurrentInventoriesFromMotherEntities(entity_id) {
  // child Entities
  const { data: childData } = useQuery(
    supabase.from('entities').select(entityColumns).eq('parent', entity_id)
  );

  // add all Entities
  const allEntities = [...(childData ? childData.map((inv) => inv.id) : [])];

  const { data, isLoading, error, isValidating } = useQuery(
    supabase
      .from('inventories')
      .select(defaultColumns)
      .in('entity_id', allEntities)
      .eq('is_current', true)
  );

  const memoizedValue = useMemo(
    () => ({
      inventories: data || [],
      inventoriesLoading: isLoading,
      inventoriesError: error,
      inventoriesValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useGetAllCurrentInventoriesFromMotherInventory(
  entity_id,
  inventory_id,
  columns = defaultColumns
) {
  // child Entities
  const {
    data: childData,
    childDataIsLoading,
    childDataError,
    childDataIsValidating,
  } = useQuery(supabase.from('entities').select(entityColumns).eq('parent', entity_id));

  const allChildid = childData ? childData.map((inv) => inv.id) : [];

  const { data, childInventoriesIsLoading, childInventoriesError, childInventoriesIsValidating } =
    useQuery(
      supabase
        .from('inventories')
        .select(defaultColumns)
        .in('entity_id', allChildid)
        .eq('is_current', true)
    );

  const isLoading = childDataIsLoading || childInventoriesIsLoading;
  const error = childDataError || childInventoriesError;
  const isValidating = childDataIsValidating || childInventoriesIsValidating;

  const memoizedValue = useMemo(
    () => ({
      inventories: data || [],
      inventoriesLoading: isLoading,
      inventoriesError: error,
      inventoriesValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export async function updateInventoryCurrent(inventoryId) {
  return supabase.from('inventories').update({ is_current: true }).eq('id', inventoryId);
}

export async function findNextCurrentInventory(entityId, currentInventoryId) {
  return supabase
    .from('inventories')
    .select('id')
    .eq('entity_id', entityId)
    .eq('is_deleted', false)
    .neq('id', currentInventoryId)
    .limit(1)
    .single();
}

export async function softDeleteInventory(inventoryId) {
  return supabase
    .from('inventories')
    .update({
      is_deleted: true,
      is_current: false,
    })
    .eq('id', inventoryId);
}
